import { traceEvent } from '@wk/wk-gatherer';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import React, { FC } from 'react';
import { Tooltip } from 'react-tooltip';

import Audio from '@/components/Icon/Audio';
import Free from '@/components/Icon/Free';
import Member from '@/components/Icon/Member';
import Video from '@/components/Icon/Video';
import useIsPc from '@/hooks/useIsPC';
import { formatSecondsToTime } from '@/utils/utils';

import { LABEL_MAP } from '../../constants';
import { Resource } from '../../types';

interface Props extends Resource {
  width: number | string;
  coverWidth?: number;
  coverHight?: number;
  hideCover?: boolean;
  eventName?: string;
  notNeedWidth?: boolean;
  urlDuration?: string;
}

const ResourceItem: FC<Props> = ({
  id,
  title,
  subjectLabel,
  abstract,
  resourceType,
  label,
  background,
  tag,
  width,
  coverHight,
  hideCover = false,
  resourceLevel = 'login',
  seoUrl,
  eventName,
  notNeedWidth,
  urlDuration,
}) => {
  const { t, i18n } = useTranslation(['resources']);
  const isPc = useIsPc();

  // const { text: resourceTypeText } = getResourceTypeContent(t, resourceType as ResourceType);

  const toDetail = () => {
    if (eventName) {
      traceEvent({
        data: {
          _event: eventName,
          subject: subjectLabel,
          material_id: id,
          options: title,
        },
      });
    }
    const language = i18n.language === 'zh' ? '/zh' : '';
    window.open(`${language}/resources/${seoUrl}_${id}detail/`, '_blank');
  };
  return (
    <>
      <figure
        onClick={toDetail}
        className="group relative box-border cursor-pointer overflow-hidden border-[1px] border-solid border-[#F0F1FC] shadow-[0px_8px_30px_0px_rgba(0,_0,_0,_0.06)] mobile:rounded-[20px] pc:rounded-[24px]"
        style={{
          border: hideCover ? '1px solid #DFE0ED' : '',
          boxShadow: hideCover ? 'none' : '',
          width: isPc || notNeedWidth ? '' : width,
        }}>
        {!hideCover && (
          <div
            className="border-b-solid relative box-border overflow-hidden border-b-[1px] border-b-[#EBECFA] mobile:rounded-t-[12px] pc:rounded-t-[24px]"
            style={{ height: coverHight ? `${coverHight / 16}rem` : '' }}>
            {label === 'hot' && (
              <div className="absolute right-[0px] top-[0px] z-10">
                {LABEL_MAP[label as 'hot' | 'free']}
              </div>
            )}
            <Image
              src={background}
              width={isPc ? (width as number) : window.innerWidth - 40}
              height={coverHight}
              className="w-full"
              alt={title}
            />
            <div className="absolute bottom-[8px] flex w-full items-center justify-between px-[8px]">
              {resourceType === 'audio' && <Audio />}
              {resourceType === 'video' && <Video />}
              {(resourceType === 'audio' || resourceType === 'video') &&
                urlDuration && (
                  <span className="inline-block rounded-[6px] bg-[rgba(27,31,77,0.40)] px-[6px] text-[14px] font-normal leading-[22px] text-white backdrop-blur-[5px]">
                    {formatSecondsToTime(Number(urlDuration))}
                  </span>
                )}
            </div>
          </div>
        )}
        <figcaption className="p-[16px] pt-[12px]">
          <div className="flex h-[48px] items-center">
            <h3
              title={title}
              className="line-clamp-2 text-[16px] font-[600] text-[#1B1F4D]">
              {title}
            </h3>
          </div>
          <div className="mt-[16px] flex h-[16px] items-center text-[14px] text-wk-words-2">
            {resourceLevel === 'login_booked' && (
              <Member className="mr-[8px]" />
            )}
            {resourceLevel === 'login' && <Free className="mr-[8px]" />}
            {/* <span className="line-clamp-1 rounded-[12px] bg-[#F5F5FC] px-[8px]">{resourceTypeText}</span> */}
            {!!tag && (
              <span className="line-clamp-1 rounded-[12px] bg-[#F5F5FC] px-[8px]">
                {tag}
              </span>
            )}
          </div>
        </figcaption>
        <div className="absolute bottom-[-202px] h-[202px] w-[100%] border-[2px] border-solid border-white bg-[linear-gradient(90deg,_rgba(255,_83,_83,_0.08)_8.55%,_rgba(109,_56,_251,_0.08)_61.14%,_rgba(0,_157,_255,_0.08)_100%),_linear-gradient(209deg,_rgba(255,_255,_255,_0.80)_18.76%,_#FFF_83.71%)] p-[16px] backdrop-blur-[10px] transition-[bottom] mobile:rounded-[20px] pc:rounded-[24px] pc:group-hover:bottom-0">
          <h3 className="line-clamp-2 text-[16px] font-[600] text-[#1B1F4D]">
            {title}
          </h3>
          {/* <p title={abstract} className="mt-[6px] line-clamp-6 text-[14px] text-[#8D8FA6]">
          {abstract}
        </p> */}
          <p
            className="mt-[6px] line-clamp-3 break-words text-[14px]  text-[#8D8FA6]"
            dangerouslySetInnerHTML={{ __html: abstract || '' }}
          />
          <div className="absolute bottom-[16px] left-0 mt-[16px] flex h-[16px] w-full items-center px-[16px] text-[14px] text-wk-words-2">
            {resourceLevel === 'login_booked' && (
              <>
                <Member data-tooltip-id={id} className="mr-[8px]" />
              </>
            )}
            {resourceLevel === 'login' && (
              <>
                <Free className="mr-[8px]" />
              </>
            )}
            {/* <span className="line-clamp-1 rounded-[12px] bg-white px-[8px]">{resourceTypeText}</span> */}
            {!!tag && (
              <span className="line-clamp-1 rounded-[12px] bg-white px-[8px]">
                {tag}
              </span>
            )}
          </div>
        </div>
      </figure>
      <Tooltip id={id}>{t('WuKong 学员专享资源')}</Tooltip>
    </>
  );
};

export default ResourceItem;
